"use client";

import { cn } from "@/lib/utils";
import {
  TestimonialCard,
  type TestimonialAuthor,
} from "@/components/ui/testimonial-card";
import { motion, useInView } from "framer-motion";
import { useRef, useEffect, useState } from "react";
import { CSSReveal } from "@/components/motion";

interface TestimonialsSectionProps {
  title?: string;
  subtitle?: string;
  description?: string;
  testimonials: Array<{
    author: TestimonialAuthor;
    quote: string;
    rating?: number;
  }>;
  className?: string;
}

export function TestimonialsSection({
  title = "Testimonials",
  subtitle = "Loved by early users",
  description = "Here's what people are saying about Tavlo.",
  testimonials,
  className,
}: TestimonialsSectionProps) {
  const sectionRef = useRef(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollPositionRef = useRef(0);
  const isHoveredRef = useRef(false);
  const isInView = useInView(sectionRef, { once: true, amount: 0.1 });
  const [isHovered, setIsHovered] = useState(false);

  // Update the ref when isHovered state changes
  useEffect(() => {
    isHoveredRef.current = isHovered;
  }, [isHovered]);

  // Horizontal scrolling with seamless loop
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const scrollSpeed = 0.5; // pixels per frame
    let animationFrameId: number;

    const scroll = () => {
      if (!isHoveredRef.current) {
        scrollPositionRef.current += scrollSpeed;

        // Calculate the width of one set of testimonials
        // Each card is 380px wide + 16px gap (1rem)
        const itemWidth = 380 + 16;
        const totalWidth = itemWidth * testimonials.length;

        // When we've scrolled past the original items, reset to start
        if (scrollPositionRef.current >= totalWidth) {
          scrollPositionRef.current = 0;
        }

        scrollContainer.scrollLeft = scrollPositionRef.current;
      }
      animationFrameId = requestAnimationFrame(scroll);
    };

    animationFrameId = requestAnimationFrame(scroll);

    return () => cancelAnimationFrame(animationFrameId);
  }, [testimonials.length]);

  return (
    <section
      ref={sectionRef}
      className={cn(
        "py-20 md:py-28 relative",
        "bg-gradient-to-b from-brand-1/[0.02] via-transparent to-brand-2/[0.02]",
        className,
      )}
    >
      <div className="mx-auto flex max-w-7xl flex-col items-center gap-12 sm:gap-16 px-4">
        {/* Section Header */}
        <div className="flex flex-col items-center gap-4 text-center">
          <CSSReveal>
            <span className="inline-flex items-center gap-2 badge-brand mb-4">
              {title}
            </span>
          </CSSReveal>
          <CSSReveal delay={0.1}>
            <h2 className="max-w-[720px] text-3xl sm:text-4xl md:text-5xl font-bold text-text-primary tracking-tight mb-4">
              {subtitle.split(" ").slice(0, -2).join(" ")}{" "}
              <motion.span
                className="bg-gradient-to-r from-brand-3 to-brand-1 bg-clip-text text-transparent inline-block"
                initial={{ filter: "blur(10px)", opacity: 0 }}
                animate={isInView ? { filter: "blur(0px)", opacity: 1 } : {}}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                {subtitle.split(" ").slice(-2).join(" ")}
              </motion.span>
            </h2>
          </CSSReveal>
          <CSSReveal delay={0.2}>
            <p className="text-lg text-text-secondary max-w-2xl">
              {description}
            </p>
          </CSSReveal>
        </div>
      </div>

      {/* Horizontal Scrolling Testimonials - Full Width */}
      <div className="relative w-full overflow-hidden">
        {/* Gradient fade on edges */}
        <div className="pointer-events-none absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-black/80 to-transparent z-10" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-black/80 to-transparent z-10" />

        {/* Scrolling container */}
        <div
          ref={scrollContainerRef}
          className="overflow-x-hidden py-4"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          style={{
            scrollBehavior: "auto",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <div
            className="flex gap-4"
            style={{
              width: "max-content",
            }}
          >
            {/* Render original testimonials */}
            {testimonials.map((testimonial, i) => (
              <TestimonialCard
                key={`original-${i}`}
                author={testimonial.author}
                quote={testimonial.quote}
                rating={testimonial.rating}
              />
            ))}
            {/* Clone testimonials for seamless loop */}
            {testimonials.map((testimonial, i) => (
              <TestimonialCard
                key={`clone-${i}`}
                author={testimonial.author}
                quote={testimonial.quote}
                rating={testimonial.rating}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Section divider */}
      <div className="max-w-7xl mx-auto px-4">
        <motion.div
          className="section-divider max-w-4xl mx-auto mt-20"
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1] }}
        />
      </div>
    </section>
  );
}
